import React, { useState } from "react";
import styled from "styled-components";

import Modal from "../../../../components/modal/Modal";
import type { TaskColumn } from "../../../../app/services/taskBoard";

/**
 * 行メニュー「行を移動」で開く移動先ボードの選択モーダル。
 * 同じプロジェクト内の編集できる別ボードから 1 枚選ぶ
 * （ノートの移動モーダル NoteMoveModal と同じ選択 UI）。
 *
 * 列タイトルの照合・不足列の確認は呼び出し側（TaskBoardSection）が行う。
 */

/** 移動先候補のボード（列照合に columns も使う） */
export interface TaskMoveTargetBoard {
  id: string;
  name: string;
  columns: TaskColumn[];
}

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-height: min(50dvh, 420px);
  overflow-y: auto;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  min-width: min(320px, calc(100vw - 80px));
`;

const OptionRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 32px;
  padding: 0 8px;
  border: none;
  border-bottom: 1px solid var(--soremo-border);
  background: ${({ $selected }) =>
    $selected ? "rgba(0, 154, 206, 0.12)" : "white"};
  color: ${({ $selected }) =>
    $selected ? "var(--soremo-blue)" : "var(--soremo-gray)"};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  .material-symbols-rounded {
    font-size: 16px;
    flex-shrink: 0;
  }

  .option-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${({ $selected }) =>
        $selected ? "rgba(0, 154, 206, 0.12)" : "var(--soremo-background)"};
    }
  }
`;

/* eslint-disable no-unused-vars -- コールバック props の引数名はドキュメント */
export interface TaskMoveModalProps {
  /** 移動する行のタスク名（タイトル表示用） */
  targetLabel: string;
  /** 移動先の候補ボード（移動元を除く、編集できるボードのみ） */
  boards: TaskMoveTargetBoard[];
  loading?: boolean;
  onMove: (board: TaskMoveTargetBoard) => void;
  onClose: () => void;
}
/* eslint-enable no-unused-vars */

const TaskMoveModal: React.FC<TaskMoveModalProps> = ({
  targetLabel,
  boards,
  loading = false,
  onMove,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = boards.find((b) => b.id === selectedId) ?? null;

  return (
    <Modal
      title={`「${targetLabel || "無題のタスク"}」の移動先`}
      titleSize="compact"
      onClose={onClose}
      primaryAction={{
        label: "移動",
        onClick: () => {
          if (selected) onMove(selected);
        },
        disabled: !selected,
        loading,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: loading,
      }}
    >
      <OptionList role="listbox" aria-label="移動先ボード">
        {boards.map((board) => (
          <OptionRow
            key={board.id}
            type="button"
            role="option"
            aria-selected={selectedId === board.id}
            $selected={selectedId === board.id}
            onClick={() => setSelectedId(board.id)}
          >
            <span className="material-symbols-rounded">table_chart</span>
            <span className="option-label bodytext-13">{board.name}</span>
          </OptionRow>
        ))}
      </OptionList>
    </Modal>
  );
};

export default TaskMoveModal;
